import React, { useCallback, useState } from 'react'
import { Alert, Box, Button, CircularProgress, IconButton, Popper, Tooltip, Typography } from '@mui/material'
import { Active, useDraggable } from '@dnd-kit/core'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import { Failure } from '../../api/failureReason'
import FailureMessage from '../../components/FailureMessage'
import { advancedColors, plannerBar } from '../../theme'
import ActionMenu from './ActionMenu'
import type { PlacedItem } from './PlannerCell'
import { TagStatus, tagLook } from './tagLook'

// Only unique, never parsed: a drag's row and item travel in its `data`.
const dragItemId = (rowKey: string, itemId: string) => `${rowKey}::${itemId}`

export type DraggedItem = { rowKey: string; itemId: string }

export const draggedItem = (active: Active): DraggedItem | undefined => {
  const { rowKey, itemId } = active.data.current ?? {}

  return typeof rowKey === 'string' && typeof itemId === 'string' ? { rowKey, itemId } : undefined
}

export type TagAction = { label: string; onClick: () => void }

// `locked`: a live enrolment the plan never placed; dragging it places it.
export type TagDecoration = {
  status: TagStatus
  label: string
  actions: TagAction[]
  locked: boolean
  // The now-line has reached this bar's planned start or end, or its track waits in intake.
  prompt?: { kind: 'start' | 'end' | 'accept'; onClick: () => void; label?: string }
  // Planned start is already past: red dashed border, plus Start and Reschedule when the plan can change.
  late?: boolean
  reschedule?: { months: { month: number; label: string }[]; onPick: (month: number) => void }
  // Its span is set by the plan, not the coach: no drag, no remove (psychiatric supervision).
  fixed?: boolean
  // Where a run accepted later than its intake starts: drawn as a line on the bar.
  acceptedAtColumn?: number
  // A track action started from this bar is in flight, or failed.
  busy?: boolean
  failure?: Failure
  onDismissFailure?: () => void
}

const barButtonSx = { minWidth: 0, px: 0.75, py: 0 }

const barCursor = (hasActions: boolean, movable: boolean) => {
  if (hasActions) {
    return 'pointer'
  }

  return movable ? 'grab' : 'default'
}

const PROMPT_NOTES = {
  start: (title: string) => `${title} has reached its planned start.`,
  end: (title: string) => `${title} is still running past its planned end.`,
  accept: (title: string) => `${title} is in intake. Accept it with a start date once its intake note is logged.`,
}

// A control on the bar must neither start a drag nor open the bar's own menu.
const barControl = (onClick: (event: React.MouseEvent<HTMLElement>) => void) => ({
  onPointerDown: (event: React.PointerEvent) => event.stopPropagation(),
  onClick: (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    onClick(event)
  },
})

type Props = {
  rowKey: string
  item: PlacedItem
  editable: boolean
  decoration?: TagDecoration
  onRemoveClick: (item: PlacedItem) => void
  // Share of the bar's width where the accepted start falls, when it falls inside the bar.
  acceptedAt?: number
}

const ItemTag = ({ rowKey, item, editable, decoration, onRemoveClick, acceptedAt }: Props) => {
  const movable = editable && !decoration?.fixed
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [rescheduleAnchor, setRescheduleAnchor] = useState<HTMLElement | null>(null)
  const [bar, setBar] = useState<HTMLElement | null>(null)
  const actions = decoration?.actions ?? []
  const cursor = barCursor(actions.length > 0, movable)
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragItemId(rowKey, item.id),
    data: { rowKey, itemId: item.id } satisfies DraggedItem,
    disabled: !movable,
  })
  const barRef = useCallback(
    (node: HTMLElement | null) => {
      setNodeRef(node)
      setBar(node)
    },
    [setNodeRef]
  )
  const prompt = decoration?.prompt
  const reschedule = decoration?.reschedule
  const look = tagLook({
    option: item.option,
    status: decoration?.status,
    endDue: prompt?.kind === 'end',
    late: Boolean(decoration?.late),
    acceptedAt,
  })
  const title = look.appearance === 'completed' ? `${item.option} - COMPLETED` : item.option
  const note = prompt ? PROMPT_NOTES[prompt.kind](item.option) : ''
  const failure = decoration?.failure

  return (
    <>
      <Tooltip title={prompt ? note : decoration?.label ?? ''}>
        <Box
          ref={barRef}
          data-due={prompt?.kind}
          data-fill={look.fillKind}
          data-outline={look.outlineKind}
          {...(movable ? listeners : {})}
          {...(movable ? attributes : {})}
          {...(actions.length > 0
            ? {
                role: 'button',
                tabIndex: 0,
                'aria-label': `${decoration?.label} — track actions`,
                'data-appearance': look.appearance,
                onClick: (event: React.MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget),
                onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
                  if (event.key === 'Enter') {
                    setMenuAnchor(event.currentTarget)
                  }
                },
              }
            : {})}
          style={{
            transform: transform ? `translate3d(${transform.x}px, 0, 0)` : undefined,
            opacity: isDragging ? plannerBar.draggingOpacity : 1,
          }}
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            borderRadius: 1.5,
            background: look.background,
            border: look.border,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            px: 1,
            cursor,
            touchAction: 'none',
          }}
        >
          {look.acceptedPct && (
            <Box
              data-testid="accepted-start"
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: look.acceptedPct,
                width: 2,
                backgroundColor: look.ink,
              }}
            />
          )}
          {prompt?.kind === 'end' && (
            <IconButton
              size="small"
              aria-label={note}
              {...barControl(prompt.onClick)}
              sx={{ mr: 0.75, p: 0.25, color: look.ink }}
            >
              <WarningAmberRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          {decoration?.status === 'completed' && (
            <CheckRoundedIcon sx={{ fontSize: 18, mr: 0.75, color: look.ink, opacity: look.contentOpacity }} />
          )}
          {look.stageTag && (
            <Typography
              variant="caption"
              sx={{
                mr: 0.75,
                px: 0.5,
                borderRadius: 0.75,
                fontWeight: 700,
                color: advancedColors.surface,
                backgroundColor: look.ink,
                lineHeight: 1.5,
              }}
            >
              {look.stageTag}
            </Typography>
          )}
          {decoration?.status === 'stalled' && (
            <Typography variant="caption" sx={{ mr: 0.75, color: advancedColors.inkMuted, fontWeight: 700 }}>
              Stalled
            </Typography>
          )}
          <Typography
            variant="caption"
            noWrap
            sx={{
              fontWeight: 600,
              color: look.ink,
              opacity: look.contentOpacity,
              pr: 2.5,
              textDecoration: look.appearance === 'closed' ? 'line-through' : 'none',
            }}
          >
            {title}
          </Typography>
          {(prompt?.kind === 'start' || prompt?.kind === 'accept') && (
            <Button
              size="small"
              variant="contained"
              aria-label={`${prompt.label ?? (prompt.kind === 'start' ? 'Open intake for' : 'Accept')} ${item.option}`}
              {...barControl(prompt.onClick)}
              sx={{ ...barButtonSx, ml: 'auto' }}
            >
              {prompt.label ?? (prompt.kind === 'start' ? 'Open intake' : 'Accept')}
            </Button>
          )}
          {decoration?.busy && (
            <CircularProgress size={14} aria-label={`Updating ${item.option}`} sx={{ ml: 'auto', color: look.ink }} />
          )}
          {reschedule && (
            <Button
              size="small"
              aria-label={`Reschedule ${item.option}`}
              {...barControl((event) => setRescheduleAnchor(event.currentTarget))}
              sx={{
                ...barButtonSx,
                ml: prompt?.kind === 'start' ? 0.5 : 'auto',
                color: advancedColors.error,
                fontWeight: 700,
              }}
            >
              Reschedule
            </Button>
          )}
          {item.comment && (
            <Tooltip title={item.comment}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 3,
                  right: 20,
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  backgroundColor: advancedColors.error,
                }}
              />
            </Tooltip>
          )}
          {movable && !decoration?.locked && (
            <IconButton
              size="small"
              {...barControl(() => onRemoveClick(item))}
              aria-label="Remove this item"
              sx={{ position: 'absolute', top: 1, right: 1, p: 0.25 }}
            >
              <RemoveRoundedIcon sx={{ fontSize: 13, color: look.ink }} />
            </IconButton>
          )}
        </Box>
      </Tooltip>
      <ActionMenu
        anchorEl={rescheduleAnchor}
        actions={(reschedule?.months ?? []).map(({ month, label }) => ({
          label,
          onClick: () => reschedule?.onPick(month),
        }))}
        onClose={() => setRescheduleAnchor(null)}
      />
      <ActionMenu anchorEl={menuAnchor} actions={actions} onClose={() => setMenuAnchor(null)} />
      <Popper
        open={Boolean(failure && bar)}
        anchorEl={bar}
        placement="bottom-start"
        sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
      >
        {failure && (
          <Alert severity="error" onClose={decoration?.onDismissFailure} sx={{ mt: 0.5, maxWidth: 400 }}>
            <FailureMessage failure={failure} />
          </Alert>
        )}
      </Popper>
    </>
  )
}

export default ItemTag
